import { compact, reduce } from 'lodash';
import ms from 'ms';
import { useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';

import type { Item, UseSliderItemsProps } from '../types';

export const useSliderItems = ({
  fetchItems,
  queryId,
}: UseSliderItemsProps) => {
  const { topicId } = useTopicContext();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    [queryId, topicId],
    ({ pageParam: page = 1, signal }) => fetchItems({ page, signal }),
    {
      getNextPageParam: (lastPage, pages) => {
        if (!lastPage || lastPage.totalItems === 0) {
          return false;
        }

        const itemsLength = pages.reduce(
          (acc, { items }) => acc + items.length,
          0,
        );

        return itemsLength < lastPage.totalItems ? pages.length + 1 : false;
      },
      onError: (_e) => {},
      staleTime: ms('5m'),
      retry: false,
    },
  );

  const items = useMemo(
    () =>
      reduce(
        compact(data?.pages),
        (acc, { items }) => acc.concat(items),
        [] as Item[],
      ),
    [data?.pages],
  );

  return {
    fetchNextPage: hasNextPage ? fetchNextPage : null,
    isError,
    isLoading: isLoading || isFetchingNextPage,
    items,
    total: data?.pages?.[0]?.totalItems ?? 0,
  };
};
